import { encodePropertyName, isRecord, jsonTemplate } from '@takeshape/util';
import camelCase from 'lodash/camelCase.js';
import get from 'lodash/get.js';
import set from 'lodash/set.js';
import { DAY_IN_SECONDS } from "../../constants.js";
import { updateAuthentication } from "../../util/authentication.js";
import { endpointWithPath, getHealthCheck } from "../../util/health.js";
import { BaseServiceProvider } from "../common/base-service-provider.js";
import { syndigoLayerSchema, syndigoProductSearchQueryArgsSchema } from "./schemas.js";
export class SyndigoServiceProvider extends BaseServiceProvider {
    serviceType = 'rest';
    introspectionType = 'syndigo';
    getConfig(formData) {
        const config = {
            id: formData.id ?? this.createId(formData),
            title: formData.title ?? this.title,
            provider: this.name,
            serviceType: this.serviceType,
            authenticationType: 'custom',
            namespace: formData.namespace,
            options: {
                endpoint: formData.endpoint,
                companyId: formData.companyId,
                recipientId: formData.recipientId
            }
        };
        return updateAuthentication(config, formData, { type: 'custom' });
    }
    isServiceConfig(serviceConfig) {
        return super.isServiceConfig(serviceConfig);
    }
    getAuthInfoRequestConfig(serviceConfig) {
        if (!this.isServiceConfig(serviceConfig)) {
            throw new Error('Invalid service config');
        }
        return {
            url: `${serviceConfig.options.endpoint}/api/auth/authenticate`,
            method: 'POST',
            json: {
                UserName: serviceConfig.authentication.username,
                Secret: serviceConfig.authentication.secret
            }
        };
    }
    getAuthInfoResult(_serviceConfig, response) {
        if (!isRecord(response)) {
            throw new Error('Response is not a record');
        }
        if (typeof response.AuthHeader !== 'string') {
            throw new Error('Response is missing AuthHeader');
        }
        return [[['headers', 'Authorization'], response.AuthHeader]];
    }
    getAuthInfoCacheExpiration() {
        return DAY_IN_SECONDS * 5;
    }
    getHealthCheck(serviceConfig) {
        return getHealthCheck({
            checkName: 'ping',
            endpoint: endpointWithPath('/api/auth/test'),
            requestMethod: 'GET',
            healthyResponseStatusCode: 200
        })(serviceConfig);
    }
    // The following methods are unique to this provider
    getProductShapeName(serviceConfig) {
        const { namespace } = serviceConfig;
        return `${namespace}_Product`;
    }
    createProductSearchQueryArgsValidator() {
        return this.createValidator(syndigoProductSearchQueryArgsSchema);
    }
    async createServiceLayer(serviceConfig) {
        if (!this.isServiceConfig(serviceConfig)) {
            throw new Error('Invalid service config');
        }
        const { id } = serviceConfig;
        const namespace = serviceConfig.namespace ? serviceConfig.namespace : '';
        const attributes = await this.#getAttributes(serviceConfig);
        const serviceSchema = jsonTemplate(syndigoLayerSchema, {
            serviceId: id,
            namespace
        });
        const typeToSchemaMap = {
            assets: {
                shapeName: 'ProductAssets',
                required: [],
                ref: `${id}:ProductAsset`
            },
            attributes: {
                shapeName: 'ProductAttributes',
                required: []
            }
        };
        for (const attr of attributes) {
            const { name, vocabAlias, attributeId, type, nullable, description } = attr;
            const propertyName = type === 'image' ? 'assets' : 'attributes';
            const { shapeName, required, ref } = typeToSchemaMap[propertyName];
            if (!nullable) {
                required.push(camelCase(name));
            }
            set(serviceSchema, ['shapes', `${namespace}_${shapeName}`, 'schema', 'properties', camelCase(name)], {
                '@ref': ref,
                type: ref ? undefined : type,
                description,
                '@mapping': `${id}:${shapeName}.${vocabAlias ? encodePropertyName(vocabAlias) : attributeId}`
            });
        }
        const productSchemaRequiredPath = ['shapes', this.getProductShapeName(serviceConfig), 'schema', 'required'];
        const productRequired = get(serviceSchema, productSchemaRequiredPath) ?? [];
        for (const [propertyName, { shapeName, required }] of Object.entries(typeToSchemaMap)) {
            if (required.length) {
                set(serviceSchema, ['shapes', `${namespace}_${shapeName}`, 'schema', 'required'], required);
                productRequired.push(propertyName);
            }
        }
        set(serviceSchema, productSchemaRequiredPath, productRequired);
        return serviceSchema;
    }
    #getDataType(dataType) {
        switch (dataType) {
            case 'Number':
                return 'number';
            case 'Image':
                return 'image';
            default:
                return 'string';
        }
    }
    async #getAttributes(serviceConfig) {
        const authInfo = this.getAuthInfoRequestConfig(serviceConfig);
        const authFetched = await fetch(authInfo.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(authInfo.json)
        });
        const authResult = (await authFetched.json());
        const authHeader = authResult.AuthHeader;
        const requirementSetsFetched = await fetch(`${serviceConfig.options.endpoint}/api/distribute/requirementset/GetByPartyIds`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader
            },
            body: JSON.stringify([serviceConfig.options.recipientId])
        });
        const requirementSets = (await requirementSetsFetched.json());
        const partialAttributeMap = new Map();
        for (const { Groups } of requirementSets) {
            for (const { Name, Attributes } of Groups) {
                for (const { AttributeId, Alias, RequiredLocales } of Attributes) {
                    partialAttributeMap.set(AttributeId, { group: Name, alias: Alias, nullable: RequiredLocales.length === 0 });
                }
            }
        }
        const productVocabAliases = new Map();
        try {
            const vocabFetched = await fetch(`${serviceConfig.options.endpoint}/api/importexport/customervocabulary/${serviceConfig.options.companyId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authHeader
                }
            });
            const vocab = (await vocabFetched.json());
            const productVocab = vocab?.VocabularyByEntityType.find((entity) => entity.EntityType === 'Product');
            if (productVocab?.SimpleAttributes) {
                for (const { Id, Alias } of productVocab.SimpleAttributes) {
                    productVocabAliases.set(Id, Alias);
                }
            }
        }
        catch {
            // If we fail to get the vocabulary, we can still proceed without it
        }
        const attributesFetched = await fetch(`${serviceConfig.options.endpoint}/api/attribute?vendorId=${serviceConfig.options.companyId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader
            },
            body: JSON.stringify([...partialAttributeMap.keys()])
        });
        const attributes = (await attributesFetched.json());
        const mappedAttributes = [];
        for (const { Partition, Description, HelpText, FormatType } of attributes) {
            const existing = partialAttributeMap.get(Partition);
            mappedAttributes.push({
                nullable: true,
                ...existing,
                attributeId: Partition,
                // This is the name that will be used in the introspection types
                name: existing?.alias ?? productVocabAliases.get(Partition) ?? Description,
                // If an alias exists in the default vocabulary, use that as the internal name for mapping rather than the UUID
                vocabAlias: productVocabAliases.get(Partition),
                description: HelpText?.trim(),
                type: this.#getDataType(FormatType)
            });
        }
        return mappedAttributes;
    }
}
